import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { DeviceConfig } from '../models/device-config';
import { customFetchV2 } from '../services/custom-fetch-v2';

interface DeviceProfile {
  id: string;
  name: string | null;
  tripAlertsEnabled: boolean;
  mapMarkerIcon: string | null;
}

interface DeviceAudio {
  enabled: boolean;
  phoneNumber: string | null;
  phoneNumberFormatted: string | null;
}

interface DevicePosition {
  latitude: number | null;
  longitude: number | null;
  speed: number;
  position_at?: Date;
  // TODO: there is more...
}

type DeviceBatteryMode = 
  | 'none'
  | 'live-tracking-10s'
  | 'live-tracking-60s'
  | 'fixed-interval-1hr'
  | 'fixed-interval-6hr'
  | 'fixed-interval-12hr';

interface Device {
  id: string;
  imei: string;
  isLocationLocked: boolean;
  isOnline: boolean;
  lastseenAt: string;
  make: string;
  model: string;
  mapMarkerIcon: string;
  name: string;
  state: string;
  audio: DeviceAudio | null;
  lastPosition: DevicePosition | null;

  /* DeviceMap positions are modified to have a 'position' set - which may be last position or a historical position. */
  position?: DevicePosition;

  batteryMode?: DeviceBatteryMode;
}

interface DeviceExtendedAttributes {
  userGuideUrl: string | null;
  features: string[] | null;
  activeProfile: DeviceProfile | null;
  latestDeviceConfig?: DeviceConfig | null;
  currentSubscription?: DeviceSubscription | null;
}

interface DeviceSubscription {
  plan: DevicePlan;
  cancelAt?: Date | null;
  isPrepaid: boolean;
  prepaidPeriodDays?: number | null;
}

interface DevicePlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: string;
  locationHistoryAccessLimitDays: number | null;
}

type DeviceFull = Device & DeviceExtendedAttributes;

/**
 * API query to retrieve user info.
 */
const getCurrentUserDevices = async (): Promise<Device[] | null> => {
  const response = await customFetchV2<Device[]>('/api/v2/devices', {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('Unable to get user devices');
  }

  return response.data;
}

/**
 * React hook that will fetch and refresh the logged in users devices.
 */
const useGetCurrentUserDevices = (): UseQueryResult<Device[], unknown> =>
  useQuery(['useGetUserDevices'], () => getCurrentUserDevices(), {
    enabled: true,
    retry: false,
    refetchInterval: 30000,
    refetchIntervalInBackground: true,
  });

/** Exports */
export { useGetCurrentUserDevices };
export type {
  Device, DeviceAudio, DeviceFull, DevicePosition, DeviceProfile, DeviceSubscription, DevicePlan,
};
